import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    IconButton,
    TextField,
    FormControlLabel,
    Switch,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Snackbar,
    Tooltip,
} from '@mui/material';
import {
    KeyboardArrowDown as ExpandIcon,
    KeyboardArrowUp as CollapseIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { adminDiagnosticsService } from '../services/adminDiagnosticsService';
import type { ClientDiagnosticsEntry } from '../types/diagnostics';

const AUTO_REFRESH_MS = 30_000;

function formatRelativeTime(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffSec = Math.floor((now - then) / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    return `${diffHr}h ${diffMin % 60}m ago`;
}

function DeviceSummary({ entry }: { entry: ClientDiagnosticsEntry }) {
    const d = entry.deviceInfo;
    if (!d) return <span>-</span>;
    // Extract browser from user agent (rough heuristic)
    let browser = 'Unknown';
    if (d.userAgent.includes('Chrome') && !d.userAgent.includes('Edg')) browser = 'Chrome';
    else if (d.userAgent.includes('Edg')) browser = 'Edge';
    else if (d.userAgent.includes('Firefox')) browser = 'Firefox';
    else if (d.userAgent.includes('Safari') && !d.userAgent.includes('Chrome')) browser = 'Safari';
    return <span>{browser} &middot; {d.screenWidth}x{d.screenHeight}</span>;
}

function ClientRow({ entry, onForceSWUpdate }: { entry: ClientDiagnosticsEntry; onForceSWUpdate: (userId: string) => void }) {
    const [open, setOpen] = useState(false);
    const hasErrors = entry.errors.length > 0;
    const hasWaitingWorker = entry.serviceWorkerInfo?.hasWaitingWorker;

    return (
        <>
            <TableRow
                hover
                onClick={() => setOpen(!open)}
                sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
            >
                <TableCell padding="checkbox">
                    <IconButton size="small">
                        {open ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{entry.email}</TableCell>
                <TableCell>
                    {entry.serviceWorkerInfo?.cacheName ?? '-'}
                    {hasWaitingWorker && (
                        <Chip label="update waiting" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                </TableCell>
                <TableCell>{entry.appVersion ?? '-'}</TableCell>
                <TableCell>
                    <DeviceSummary entry={entry} />
                </TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.currentPage ?? '-'}
                </TableCell>
                <TableCell>{formatRelativeTime(entry.lastHeartbeat)}</TableCell>
                <TableCell>
                    {hasErrors ? (
                        <Chip label={entry.errors.length} size="small" color="error" />
                    ) : (
                        <Chip label="0" size="small" color="success" variant="outlined" />
                    )}
                </TableCell>
                <TableCell>
                    <Chip
                        label={entry.isOnline ? 'Online' : 'Offline'}
                        size="small"
                        color={entry.isOnline ? 'success' : 'default'}
                        variant={entry.isOnline ? 'filled' : 'outlined'}
                    />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={!entry.isOnline ? 'Client is offline' : 'Force service worker update'}>
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={!entry.isOnline}
                                onClick={() => onForceSWUpdate(entry.userId)}
                            >
                                Force SW Update
                            </Button>
                        </span>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={10} sx={{ py: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2 }}>
                            {/* Device Info */}
                            {entry.deviceInfo && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Device Info
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, width: 180 }}>User Agent</TableCell>
                                                <TableCell sx={{ wordBreak: 'break-all' }}>{entry.deviceInfo.userAgent}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Screen</TableCell>
                                                <TableCell>{entry.deviceInfo.screenWidth}x{entry.deviceInfo.screenHeight} @{entry.deviceInfo.devicePixelRatio}x</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                                                <TableCell>{entry.deviceInfo.platform}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Language</TableCell>
                                                <TableCell>{entry.deviceInfo.language}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Time Zone</TableCell>
                                                <TableCell>{entry.deviceInfo.timeZone}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            {/* SW Info */}
                            {entry.serviceWorkerInfo && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Service Worker Info
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, width: 180 }}>Cache Name</TableCell>
                                                <TableCell>{entry.serviceWorkerInfo.cacheName ?? 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                                                <TableCell>{entry.serviceWorkerInfo.state}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Waiting Worker</TableCell>
                                                <TableCell>{entry.serviceWorkerInfo.hasWaitingWorker ? 'Yes' : 'No'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Cache Entries</TableCell>
                                                <TableCell>{entry.serviceWorkerInfo.cacheEntryCount}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            {/* Errors */}
                            {entry.errors.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Errors ({entry.errors.length})
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 300 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Time</TableCell>
                                                    <TableCell>Type</TableCell>
                                                    <TableCell>Page</TableCell>
                                                    <TableCell>Message</TableCell>
                                                    <TableCell>Source</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {[...entry.errors].reverse().map((err, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            {new Date(err.timestamp).toLocaleTimeString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={err.type} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{err.currentPage ?? '-'}</TableCell>
                                                        <TableCell sx={{ maxWidth: 400, wordBreak: 'break-word' }}>
                                                            {err.message}
                                                            {err.stack && (
                                                                <Typography
                                                                    variant="caption"
                                                                    component="pre"
                                                                    sx={{ mt: 0.5, color: 'text.secondary', whiteSpace: 'pre-wrap', fontSize: '0.7rem' }}
                                                                >
                                                                    {err.stack}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{err.source ?? '-'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {entry.errors.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No errors reported.
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function AdminDiagnosticsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = useState<ClientDiagnosticsEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

    const handleForceSWUpdate = async (userId: string) => {
        try {
            await adminDiagnosticsService.forceServiceWorkerUpdate(userId);
            setSnackbar({ open: true, message: 'Force SW update command sent.', severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: 'Failed to send force SW update command.', severity: 'error' });
        }
    };

    const handleForceSWUpdateAll = async () => {
        try {
            await adminDiagnosticsService.forceServiceWorkerUpdateAll();
            setSnackbar({ open: true, message: 'Force SW update command sent to all clients.', severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: 'Failed to send force SW update command.', severity: 'error' });
        }
    };

    const fetchClients = useCallback(async () => {
        try {
            const data = await adminDiagnosticsService.getClients();
            setClients(data);
            setError(null);
        } catch (err: any) {
            setError(err?.response?.status === 403 ? 'Access denied. Admin only.' : 'Failed to load diagnostics data.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Admin guard
    useEffect(() => {
        if (!user?.isAdmin) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    // Initial fetch
    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    // Auto-refresh
    useEffect(() => {
        if (!autoRefresh) return;
        const id = setInterval(fetchClients, AUTO_REFRESH_MS);
        return () => clearInterval(id);
    }, [autoRefresh, fetchClients]);

    const filtered = clients.filter(
        (c) =>
            c.email.toLowerCase().includes(search.toLowerCase()) ||
            c.userId.toLowerCase().includes(search.toLowerCase()),
    );

    if (!user?.isAdmin) return null;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Client Diagnostics</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search by email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <FormControlLabel
                        control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
                        label="Auto-refresh"
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleForceSWUpdateAll}
                    >
                        Force SW Update All
                    </Button>
                    <IconButton onClick={fetchClients} title="Refresh now">
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Email</TableCell>
                                <TableCell>SW Cache</TableCell>
                                <TableCell>App Version</TableCell>
                                <TableCell>Device</TableCell>
                                <TableCell>Current Page</TableCell>
                                <TableCell>Last Seen</TableCell>
                                <TableCell>Errors</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            {clients.length === 0
                                                ? 'No connected clients. Data will appear once clients send their first heartbeat.'
                                                : 'No clients match the search filter.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((entry) => <ClientRow key={entry.userId} entry={entry} onForceSWUpdate={handleForceSWUpdate} />)
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Showing {filtered.length} of {clients.length} connected clients.
                {autoRefresh && ' Auto-refreshing every 30s.'}
            </Typography>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
